"use client"

import { useState, useRef, useEffect } from "react"
import { Play, Pause, Volume2, VolumeX } from "lucide-react"
import { Button } from "@/components/ui/button"

interface ProductVideoProps {
  productType: string
  className?: string
}

export default function ProductVideo({ productType, className }: ProductVideoProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const [isLoaded, setIsLoaded] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  // Generate video URL based on product type
  const getVideoUrl = (type: string) => {
    const videoMap: { [key: string]: string } = {
      Valves: "/placeholder.svg?height=400&width=400&text=Valves+Video",
      Pipes: "/placeholder.svg?height=400&width=400&text=Pipes+Video",
      "Plates & Sheets": "/placeholder.svg?height=400&width=400&text=Plates+Video",
      "H-Iron": "/placeholder.svg?height=400&width=400&text=H-Iron+Video",
      Boxbars: "/placeholder.svg?height=400&width=400&text=Boxbars+Video",
      "Nuts & Bolts": "/placeholder.svg?height=400&width=400&text=Nuts+Bolts+Video",
      "GI Conduits": "/placeholder.svg?height=400&width=400&text=GI+Conduits+Video",
    }
    return videoMap[type] || "/placeholder.svg?height=400&width=400&text=Product+Video"
  }

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted
      setIsMuted(!isMuted)
    }
  }

  useEffect(() => {
    const video = videoRef.current
    if (video) {
      const handleLoadedData = () => setIsLoaded(true)
      const handlePlay = () => setIsPlaying(true)
      const handlePause = () => setIsPlaying(false)

      video.addEventListener("loadeddata", handleLoadedData)
      video.addEventListener("play", handlePlay)
      video.addEventListener("pause", handlePause)

      return () => {
        video.removeEventListener("loadeddata", handleLoadedData)
        video.removeEventListener("play", handlePlay)
        video.removeEventListener("pause", handlePause)
      }
    }
  }, [])

  return (
    <div className={`relative w-full h-full bg-slate-900 rounded-lg overflow-hidden ${className}`}>
      {/* Placeholder for video - in real implementation, this would be an actual video */}
      <div className="relative w-full h-full bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Play className="h-8 w-8" />
          </div>
          <p className="text-sm opacity-80">{productType} Demo Video</p>
        </div>
      </div>

      {/* Video Controls Overlay */}
      <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
        <div className="flex items-center space-x-4">
          <Button
            size="sm"
            variant="secondary"
            className="bg-white/90 text-slate-900 hover:bg-white"
            onClick={togglePlay}
          >
            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="bg-white/90 text-slate-900 hover:bg-white"
            onClick={toggleMute}
          >
            {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Loading indicator */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-slate-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      )}
    </div>
  )
}
